import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export interface PresignedUrlResponse {
  success: boolean;
  presignedUrl?: string;
  expiresIn?: number;
  error?: string;
}

export const fileAccessApi = {
  /**
   * Get a pre-signed URL for file access
   */
  getPresignedUrl: async (fileUrl: string, expiresIn?: number): Promise<PresignedUrlResponse> => {
    try {
      const params = new URLSearchParams();
      params.append('fileUrl', fileUrl);
      if (expiresIn) {
        params.append('expiresIn', expiresIn.toString());
      }

      const response = await axios.get(`${API_URL}/files/presigned?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error getting pre-signed URL:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get pre-signed URL',
      };
    }
  },

  /**
   * Get the secure file URL for display/download
   */
  getSecureFileUrl: async (fileUrl: string): Promise<string> => {
    // If it's already a complete URL (local file), return as-is
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      return fileUrl;
    }

    // If it's an S3 reference URL, get a pre-signed URL
    if (fileUrl.startsWith('s3://')) {
      const response = await fileAccessApi.getPresignedUrl(fileUrl, 3600); // 1 hour
      if (response.success && response.presignedUrl) {
        return response.presignedUrl;
      }
      throw new Error(response.error || 'Failed to get pre-signed URL');
    }

    // For local files, return the API endpoint (query param avoids proxy %2F issues)
    return `${API_URL}/files/stream?fileId=${encodeURIComponent(fileUrl)}`;
  },

  /**
   * Get download URL for a file
   */
    getDownloadUrl: async (fileUrl: string): Promise<string> => {
    // If it's already a complete URL (local file), return as-is
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      return fileUrl;
    }

    // If it's an S3 reference URL, get a pre-signed URL
    if (fileUrl.startsWith('s3://')) {
      const response = await fileAccessApi.getPresignedUrl(fileUrl, 3600); // 1 hour
      if (response.success && response.presignedUrl) {
        return response.presignedUrl;
      }
      throw new Error(response.error || 'Failed to get pre-signed URL');
    }

    // For local files, return the download endpoint (query param avoids proxy %2F issues)
    return `${API_URL}/files/download?fileId=${encodeURIComponent(fileUrl)}`;
  },
};